import React, { useEffect } from 'react';
import { ClipLoader } from 'react-spinners';
import { notification } from 'antd';

import { getContractsByStudent } from '../../redux/api/course';

import arrowPrev from '../../img/arrowPrev.svg';
import fileTextIcon from '../../img/FileText.svg';

import {
  Overlay,
  ModalCard,
  ModalHeader,
  ModalBody,
  ContractsList,
  ContractItem,
  ContractIconWrapper,
  ContractInfo,
  ContractCourse,
  ContractTurma,
  ContractDate,
  ViewButton,
  EmptyState,
  EmptyIcon,
  LoadingWrapper,
  TermsItem,
} from './styles';
import config from '../../config';

type Props = {
  setVisible: (visible: boolean) => void;
};

function ContractsModal({ setVisible }: Props) {
  const [selectedContractUrl, setSelectedContractUrl] = React.useState<string | null>(null);
  const [contractsList, setContractsList] = React.useState<any[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);

  useEffect(() => {
    // Rota dedicada: mantém contratos de cursos dos quais o aluno foi removido,
    // gratuitos e adicionais (a findCoursesByStudent descarta esses casos).
    let cancelled = false;

    getContractsByStudent()
      .then((contracts: any[]) => {
        if (!cancelled) setContractsList(Array.isArray(contracts) ? contracts : []);
      })
      .catch(() => {
        if (!cancelled) {
          notification['error']({
            message: `Problemas ao listar os contratos, por favor contate o administrador.`,
          });
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleClose = () => setVisible(false);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const handleOpenContract = (contractUrl: string) => {
    setSelectedContractUrl(contractUrl);
  };

  const renderAcceptTermsItem = () => {
    return (
      <TermsItem
        href={config.REACT_APP_PRIVACY_POLICY_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        <ContractIconWrapper>
          <img src={fileTextIcon} alt="" />
        </ContractIconWrapper>
        <ContractInfo>
          <ContractCourse>Termos de Aceite</ContractCourse>
          <ContractTurma>Política de privacidade</ContractTurma>
        </ContractInfo>
        <ViewButton as="span">Ver termos</ViewButton>
      </TermsItem>
    );
  };

  return (
    <Overlay onClick={handleOverlayClick}>
      <ModalCard $isContractOpen={!!selectedContractUrl}>
        <ModalHeader>
          <button
            type="button"
            onClick={selectedContractUrl ? () => setSelectedContractUrl(null) : handleClose}
            aria-label={selectedContractUrl ? 'Voltar' : 'Fechar'}
          >
            <img src={arrowPrev} alt={selectedContractUrl ? 'Voltar' : 'Fechar'} />
          </button>
          <h3>Contratos e Termos de Aceite</h3>
        </ModalHeader>

        <ModalBody $isContractOpen={!!selectedContractUrl}>
          {isLoading && contractsList.length === 0 ? (
            <LoadingWrapper>
              <ClipLoader size={32} color="#5768ff" />
            </LoadingWrapper>
          ) : contractsList.length === 0 ? (
            <>
              <EmptyState>
                <EmptyIcon>
                  <img src={fileTextIcon} alt="" />
                </EmptyIcon>
                <p>Nenhum contrato disponível no momento.</p>
              </EmptyState>
              {renderAcceptTermsItem()}
            </>
          ) : selectedContractUrl ? (
            <iframe
              src={selectedContractUrl}
              title="Contrato"
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          ) : (
            <ContractsList>
              {contractsList.map((course) => (
                <ContractItem
                  key={course.id_order ?? course.id_turma ?? course.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOpenContract(course.contrato_url)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleOpenContract(course.contrato_url);
                    }
                  }}
                >
                  <ContractIconWrapper>
                    <img src={fileTextIcon} alt="" />
                  </ContractIconWrapper>
                  <ContractInfo>
                    <ContractCourse>{course.curso}</ContractCourse>
                    <ContractTurma>{course.turma}</ContractTurma>
                    {course.data_compra && (
                      <ContractDate>Compra em {formatDate(course.data_compra)}</ContractDate>
                    )}
                  </ContractInfo>
                  <ViewButton>Ver contrato</ViewButton>
                </ContractItem>
              ))}
              {renderAcceptTermsItem()}
            </ContractsList>
          )}
        </ModalBody>
      </ModalCard>
    </Overlay>
  );
}

export default ContractsModal;
